import {
  useMemo,
  useRef,
  useState,
  FocusEvent,
  MouseEvent,
  useEffect,
  useCallback
} from "react";
import { v4 as uuid } from "uuid";
import { DoNotCare } from "../../types";
import {
  SelectProps,
  isPropsMultiSelect,
  isPropsSingleSelect,
  SelectTargetSetFocusRef
} from "./types";
import SelectTarget from "./Target";
import FormField from "../FormField";
import { styles } from "./styles";
import Dropdown, { DropdownProps } from "../Dropdown";
import { useSizingProp } from "../../hooks/props/useSizingProp";
import ReadonlyFormField from "../FormField/Readonly";
import { useSelectedIds, useItemsMap } from "../../hooks";

/** `Select` allows users to select from a list of options  */
const Select = <
  Selected extends string[] | string | undefined,
  Item extends Record<string, DoNotCare> | string
>({
  fill,
  options,
  fixed: incomingFixed,
  width,
  minWidth,
  height: incomingHeight,
  lockedWidth = false,
  fillToWidestOption,
  flyoutProps = {},
  value: incomingValue,
  label,
  hideLabel,
  required,
  error,
  offsetError,
  description,
  labelSuffix,
  intent,
  labelSuffixAlign,
  cannotClear,
  canSelectAll = false,
  pinned,
  searchable,
  search: incomingSearch,
  disabled,
  readonly = false,
  focusOnMount,
  readonlyParser,
  id = `kit-${uuid()}`,
  max,
  lookupId,
  lookupLabel,
  lookupIcon,
  lookupIntent,
  onChange,
  onClick,
  onFocus,
  onBlur,
  selectedSeparator,
  hideSelected,
  isVirtualized = options.length > 100,
  noWrap,
  isLoading
}: SelectProps<Selected, Item>) => {
  const [isOpen, setIsOpen] = useState(!!flyoutProps?.isOpen);

  const selectProps: Pick<
    DropdownProps<Selected, Item>,
    "value" | "onChange"
  > = {
    value: incomingValue,
    onChange: (value, items) => {
      if ((Array.isArray(value) && value.length === 0) || !value) {
        setIsOpen(true);
      }
      onChange?.(value, items);
    }
  };

  const baseRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [targetWidth, setTargetWidth] = useState<number>(0);

  let setTargetFocusRef: SelectTargetSetFocusRef | null = null;

  const height = useSizingProp(incomingHeight, "auto");

  const itemsMap = useItemsMap<Item>(options, lookupId);

  const { selectedIds, fixed } = useSelectedIds(incomingValue, incomingFixed);

  useEffect(() => {
    setIsOpen(!!flyoutProps?.isOpen);
  }, [flyoutProps?.isOpen]);

  const focusLastChipOrTarget = () => {
    if (
      Array.isArray(incomingValue) &&
      selectedIds.length !== (fixed?.size || 0)
    ) {
      return setTargetFocusRef?.chipIndex?.(selectedIds.length - 1);
    }
    setTargetFocusRef?.target();
  };

  const focusSearchOrFirstItem = () => {
    if (!isOpen) {
      return setIsOpen(true);
    }
    if (searchable) {
      return dropdownRef?.current
        ?.querySelector<HTMLInputElement>(".kit-Input input")
        ?.focus();
    }
    dropdownRef?.current
      ?.querySelector<HTMLOptionElement>(
        // first option that is not disabled
        "[aria-selected]:not([aria-disabled=true])"
      )
      ?.focus();
  };

  const onTargetClick = (e: MouseEvent<HTMLElement>) => {
    setIsOpen(prev => !prev);
    onClick?.(e);
  };

  const onTargetBlur = (
    direction: "next" | "prev" | undefined,
    event: FocusEvent<HTMLElement> | undefined
  ) => {
    if (direction === "next") {
      focusSearchOrFirstItem();
    }
    if (direction === "prev") {
      setIsOpen(false);
      setTargetFocusRef?.target();
    }
    if (event) {
      onBlur?.(event);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    flyoutProps?.onClose?.();
  };

  const search = (() => {
    const searchProps = {
      ...incomingSearch,
      focusOnMount: incomingSearch?.focusOnMount !== false
    };
    if (typeof options[0] === "string") {
      return searchProps as SelectProps<Selected, Item>["search"];
    }
    return {
      ...searchProps,
      keys: incomingSearch?.keys || []
    } as SelectProps<Selected, Item>["search"];
  })();

  const dropdownFlyoutProps: SelectProps<Selected, Item>["flyoutProps"] =
    useMemo(
      () => ({
        position: "bottom",
        offset: "0, 4px",
        keepOpen: Array.isArray(incomingValue),
        usePortal: false,
        ...flyoutProps,
        isOpen,
        minimal: true,
        onClose
      }),
      [flyoutProps, isOpen, incomingValue]
    );

  const handleOnDropdownBlur = (direction: "next" | "prev") => {
    switch (direction) {
      case "next":
        setIsOpen(false);
        break;
      case "prev": {
        focusLastChipOrTarget();
        break;
      }
      default:
        break;
    }
  };

  const targetAriaLabel = `${label}${required ? " *" : ""}`;

  const handleReadonlyParse = useCallback(
    (selected: Selected) => {
      if (readonlyParser) {
        return readonlyParser(selected);
      }
      if (lookupLabel) {
        const readOnlyValues = selectedIds.map(id => {
          const item = itemsMap.get(id);
          return item ? (lookupLabel(item) ?? id) : id;
        });
        if (
          readOnlyValues.every(
            value => typeof value === "string" || typeof value === "number"
          )
        ) {
          return readOnlyValues.join(", ");
        }
        return readOnlyValues;
      }
      return selectedIds.join(", ");
    },
    [readonlyParser, selectedIds, itemsMap, lookupLabel]
  );

  if (readonly) {
    return (
      <ReadonlyFormField
        parser={handleReadonlyParse}
        fill={fill}
        label={label}
        hideLabel={hideLabel}
        value={incomingValue}
        minWidth={`${targetWidth}px`}
        description={description}
        labelSuffix={labelSuffix}
        labelSuffixAlign={labelSuffixAlign}
      />
    );
  }

  return (
    <div
      ref={baseRef}
      className="kit-Select"
      css={[!!fill && styles.fill]}
      id={id}
    >
      <FormField
        fieldset
        label={label}
        hideLabel={hideLabel}
        error={error}
        offsetError={offsetError}
        required={required}
        description={description}
        labelSuffix={labelSuffix}
        labelSuffixAlign={labelSuffixAlign}
      >
        {isPropsSingleSelect(selectProps) && (
          <Dropdown
            containerRef={ref => {
              dropdownRef.current = ref;
            }}
            flyoutProps={dropdownFlyoutProps}
            fill={fill}
            cannotClear={cannotClear}
            items={options}
            value={selectProps.value}
            hideSelected={hideSelected}
            width={targetWidth}
            minWidth={targetWidth}
            maxWidth={lockedWidth ? targetWidth : undefined}
            height={height}
            lookupId={lookupId}
            lookupLabel={lookupLabel}
            lookupIcon={lookupIcon}
            onChange={(value, items) => {
              selectProps.onChange(value, items);
              setIsOpen(!value);
            }}
            searchable={searchable}
            search={search}
            onDropdownBlur={handleOnDropdownBlur}
            isVirtualized={isVirtualized}
            isLoading={isLoading}
          >
            <SelectTarget
              cannotClear={cannotClear}
              options={options}
              value={selectProps.value}
              onChange={selectProps.onChange}
              selectedIds={selectedIds}
              lookupLabel={lookupLabel}
              lookupId={lookupId}
              lookupIntent={lookupIntent}
              isOpen={isOpen}
              onBlur={onTargetBlur}
              focusOnMount={focusOnMount}
              width={width}
              minWidth={minWidth}
              fill={fill}
              fillToWidestOption={fillToWidestOption}
              label={targetAriaLabel}
              disabled={!!disabled}
              error={!!error}
              onFocus={onFocus}
              onClick={onTargetClick}
              setTargetWidth={setTargetWidth}
              itemsMap={itemsMap}
              setFocusRef={ref => (setTargetFocusRef = ref)}
            />
          </Dropdown>
        )}
        {isPropsMultiSelect(selectProps) && (
          <Dropdown
            containerRef={ref => {
              dropdownRef.current = ref;
            }}
            flyoutProps={dropdownFlyoutProps}
            fill={fill}
            pinned={pinned}
            items={options}
            value={selectedIds}
            fixed={fixed}
            width={targetWidth}
            minWidth={targetWidth}
            maxWidth={lockedWidth ? targetWidth : undefined}
            height={height}
            lookupId={lookupId}
            lookupLabel={lookupLabel}
            lookupIcon={lookupIcon}
            onChange={selectProps.onChange}
            searchable={searchable}
            search={search}
            max={max}
            onDropdownBlur={handleOnDropdownBlur}
            isVirtualized={isVirtualized}
            isLoading={isLoading}
            canSelectAll={canSelectAll}
          >
            <SelectTarget
              options={options}
              value={selectProps.value}
              fixed={fixed}
              onChange={selectProps.onChange}
              selectedIds={selectedIds}
              lookupLabel={lookupLabel}
              lookupId={lookupId}
              lookupIntent={lookupIntent}
              intent={intent}
              lookupIcon={lookupIcon}
              isOpen={isOpen}
              onBlur={onTargetBlur}
              focusOnMount={focusOnMount}
              width={width}
              minWidth={minWidth}
              fill={fill}
              fillToWidestOption={fillToWidestOption}
              label={targetAriaLabel}
              disabled={!!disabled}
              error={!!error}
              onFocus={onFocus}
              onClick={onTargetClick}
              setTargetWidth={setTargetWidth}
              setFocusRef={ref => (setTargetFocusRef = ref)}
              itemsMap={itemsMap}
              selectedSeparator={selectedSeparator}
              noWrap={noWrap}
            />
          </Dropdown>
        )}
      </FormField>
    </div>
  );
};

export default Select;
export * from "./types";
